// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { ActionUnion, createAction, ThunkAction } from 'utils/redux';
import { getCore, ReferenceJob } from 'cvat-core-wrapper';
import { ReferenceJobsQuery } from 'reducers';
import { filterNull } from 'utils/filter-null';
import { ReferenceJobData } from 'components/create-reference-job-page/reference-job-form';

const cvat = getCore();

export enum ReferenceJobsActionTypes {
    GET_JOBS = 'GET_JOBS',
    GET_JOBS_SUCCESS = 'GET_JOBS_SUCCESS',
    GET_JOBS_FAILED = 'GET_JOBS_FAILED',
    GET_JOB_PREVIEW = 'GET_JOB_PREVIEW',
    GET_JOB_PREVIEW_SUCCESS = 'GET_JOB_PREVIEW_SUCCESS',
    GET_JOB_PREVIEW_FAILED = 'GET_JOB_PREVIEW_FAILED',
    CREATE_JOB_FAILED = 'CREATE_JOB_FAILED',
    UPDATE_JOB = 'UPDATE_JOB',
    UPDATE_JOB_SUCCESS = 'UPDATE_JOB_SUCCESS',
    UPDATE_JOB_FAILED = 'UPDATE_JOB_FAILED',
    DELETE_JOB = 'DELETE_JOB',
    DELETE_JOB_SUCCESS = 'DELETE_JOB_SUCCESS',
    DELETE_JOB_FAILED = 'DELETE_JOB_FAILED',
}

interface JobsList extends Array<any> {
    count: number;
}

const referenceJobsActions = {
    getReferenceJobs: (query: Partial<ReferenceJobsQuery>) => createAction(ReferenceJobsActionTypes.GET_JOBS, { query }),
    getReferenceJobsSuccess: (jobs: JobsList) => createAction(ReferenceJobsActionTypes.GET_JOBS_SUCCESS, { jobs }),
    getReferenceJobsFailed: (error: any) => createAction(ReferenceJobsActionTypes.GET_JOBS_FAILED, { error }),
    getReferenceJobPreview: (jobID: number) => createAction(ReferenceJobsActionTypes.GET_JOB_PREVIEW, { jobID }),
    getReferenceJobPreviewSuccess: (jobID: number, preview: string) => createAction(ReferenceJobsActionTypes.GET_JOB_PREVIEW_SUCCESS, { jobID, preview }),
    getReferenceJobPreviewFailed: (jobID: number, error: any) => createAction(ReferenceJobsActionTypes.GET_JOB_PREVIEW_FAILED, { jobID, error }),
    createReferenceJobFailed: (error: any) => createAction(ReferenceJobsActionTypes.CREATE_JOB_FAILED, { error }),
    updateReferenceJob: () => createAction(ReferenceJobsActionTypes.UPDATE_JOB),
    updateReferenceJobSuccess: (job: ReferenceJob) => createAction(ReferenceJobsActionTypes.UPDATE_JOB_SUCCESS, { job }),
    updateReferenceJobFailed: (jobID: number, error: any) => createAction(ReferenceJobsActionTypes.UPDATE_JOB_FAILED, { jobID, error }),
    deleteReferenceJob: (jobID: number) => createAction(ReferenceJobsActionTypes.DELETE_JOB, { jobID }),
    deleteReferenceJobSuccess: (jobID: number) => createAction(ReferenceJobsActionTypes.DELETE_JOB_SUCCESS, { jobID }),
    deleteReferenceJobFailed: (jobID: number, error: any) => createAction(ReferenceJobsActionTypes.DELETE_JOB_FAILED, { jobID, error }),
};

export type ReferenceJobsActions = ActionUnion<typeof referenceJobsActions>;

export const getReferenceJobsAsync =
    (query: ReferenceJobsQuery): ThunkAction => async (dispatch) => {
        try {
            // We remove all keys with null values from the query
            const filteredQuery = filterNull(query);

            dispatch(referenceJobsActions.getReferenceJobs(filteredQuery as ReferenceJobsQuery));
            const jobs = await cvat.reference_jobs.get(filteredQuery);
            dispatch(referenceJobsActions.getReferenceJobsSuccess(jobs));
        } catch (error) {
            dispatch(referenceJobsActions.getReferenceJobsFailed(error));
        }
    };

export const getReferenceJobPreviewAsync =
    (job: ReferenceJob): ThunkAction => async (dispatch) => {
        dispatch(referenceJobsActions.getReferenceJobPreview(job.id));
        try {
            const result = await job.frames.preview();
            dispatch(referenceJobsActions.getReferenceJobPreviewSuccess(job.id, result));
        } catch (error) {
            dispatch(referenceJobsActions.getReferenceJobPreviewFailed(job.id, error));
        }
    };

export const createReferenceJobAsync =
    (data: ReferenceJobData): ThunkAction => async (dispatch) => {
        const jobInstance = new cvat.classes.ReferenceJob(data as any);
        try {
            const savedJob = await jobInstance.save(data);
            return savedJob;
        } catch (error) {
            dispatch(referenceJobsActions.createReferenceJobFailed(error));
            throw error;
        }
    };

export function updateReferenceJobAsync(jobInstance: ReferenceJob): ThunkAction<Promise<boolean>> {
    return async (dispatch): Promise<boolean> => {
        try {
            dispatch(referenceJobsActions.updateReferenceJob());
            const updated = await jobInstance.save();
            dispatch(referenceJobsActions.updateReferenceJobSuccess(updated));
        } catch (error) {
            dispatch(referenceJobsActions.updateReferenceJobFailed(jobInstance.id, error));
            return false;
        }

        return true;
    };
}

export const deleteReferenceJobAsync =
    (job: ReferenceJob): ThunkAction => async (dispatch) => {
        dispatch(referenceJobsActions.deleteReferenceJob(job.id));
        try {
            await job.delete();
        } catch (error) {
            dispatch(referenceJobsActions.deleteReferenceJobFailed(job.id, error));
            return;
        }

        dispatch(referenceJobsActions.deleteReferenceJobSuccess(job.id));
    };
